/**
 * 카드 등록 모달
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 *
 * Bloomin 토큰화 API로 새 카드를 등록합니다.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { X, CreditCard, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { logger } from '@/utils/logger';
import type {
  BloominCardRegistrationPayload,
  CardRegistrationSession,
} from '@/lib/api/types';
import {
  buildBloominCardRegistrationPayload,
  canRenderRegistrationSession,
  initialBloominCardForm,
  type BloominCardFieldErrors,
  type BloominCardFormState,
} from './bloominRegistration';
import { BloominCardFields } from './BloominCardFields';

type RegistrationStep = 'init' | 'form' | 'success' | 'error';

const toSafeBloominErrorLog = (err: unknown) => ({
  name: err instanceof Error ? err.name : typeof err,
});

const INPUT_ERROR_FIELD: Record<keyof BloominCardFormState, keyof BloominCardFieldErrors> = {
  cardNumber: 'cardNumber',
  expiryMonth: 'expiry',
  expiryYear: 'expiry',
  cvv: 'cvv',
  holder: 'holder',
};

export interface AddCardModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 체크아웃 정보 (createCheckout 결과) */
  checkout: CardRegistrationSession | null;
  /** 체크아웃 생성 중 */
  isCreatingCheckout?: boolean;
  /** 등록 확인 중 */
  isConfirming?: boolean;
  /** 모달 닫기 */
  onClose: () => void;
  /** 체크아웃 생성 요청 - checkout 데이터를 반환하거나 void (부모가 상태를 관리할 경우) */
  onCreateCheckout: () => Promise<CardRegistrationSession | void>;
  /** Bloomin 카드 등록 요청 */
  onConfirmBloominRegistration: (
    cardPayload: BloominCardRegistrationPayload,
    nickname?: string
  ) => Promise<void>;
  /** 번역 텍스트 */
  t: {
    title: string;
    subtitle: string;
    loading: string;
    creatingCheckout: string;
    confirmingRegistration: string;
    successTitle: string;
    successMessage: string;
    errorTitle: string;
    errorMessage: string;
    retry: string;
    done: string;
    cancel: string;
    nicknameLabel: string;
    nicknamePlaceholder: string;
    skipNickname: string;
    saveWithNickname: string;
    cardNumberLabel: string;
    expiryMonthLabel: string;
    expiryYearLabel: string;
    cvvLabel: string;
    holderLabel: string;
    invalidCard: string;
    invalidExpiry: string;
    invalidCvv: string;
    invalidHolder: string;
  };
}

export function AddCardModal({
  isOpen,
  checkout,
  isCreatingCheckout = false,
  isConfirming = false,
  onClose,
  onCreateCheckout,
  onConfirmBloominRegistration,
  t,
}: AddCardModalProps) {
  const [step, setStep] = useState<RegistrationStep>('init');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bloominCard, setBloominCard] = useState<BloominCardFormState>(initialBloominCardForm);
  const [bloominFieldErrors, setBloominFieldErrors] = useState<BloominCardFieldErrors>({});

  /**
   * 초기화
   */
  const resetState = useCallback(() => {
    setStep('init');
    setNickname('');
    setError(null);
    setBloominCard(initialBloominCardForm);
    setBloominFieldErrors({});
  }, []);

  /**
   * 모달이 열릴 때 자동으로 체크아웃 생성
   * 의존성 배열에서 함수를 제외하고 상태만 감시하여 무한 루프 방지
   */
  const hasCalledCheckoutRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // 모달이 닫힐 때 상태 초기화
      setStep('init');
      setNickname('');
      setError(null);
      setBloominCard(initialBloominCardForm);
      setBloominFieldErrors({});
      hasCalledCheckoutRef.current = false;
      return;
    }

    // 체크아웃이 이미 있으면 form 단계로 전환
    if (checkout) {
      if (!canRenderRegistrationSession(checkout)) {
        setError(t.errorMessage);
        setStep('error');
        return;
      }

      setStep('form');
      return;
    }

    // 체크아웃 생성 중이면 대기
    if (isCreatingCheckout) {
      return;
    }

    // 체크아웃 생성 (step이 init일 때만, 한 번만)
    if (step === 'init' && !hasCalledCheckoutRef.current) {
      hasCalledCheckoutRef.current = true;
      onCreateCheckout().catch((err) => {
        setError(err instanceof Error ? err.message : t.errorMessage);
        setStep('error');
      });
    }
  }, [isOpen, checkout, isCreatingCheckout, step, onCreateCheckout, t.errorMessage]);

  const clearBloominFieldError = useCallback((field: keyof BloominCardFormState) => {
    const errorField = INPUT_ERROR_FIELD[field];
    setBloominFieldErrors((prev) => {
      if (!prev[errorField]) return prev;
      const next = { ...prev };
      delete next[errorField];
      return next;
    });
  }, []);

  const buildBloominPayload = useCallback((): BloominCardRegistrationPayload | null => {
    const result = buildBloominCardRegistrationPayload(bloominCard, {
      invalidCard: t.invalidCard,
      invalidExpiry: t.invalidExpiry,
      invalidCvv: t.invalidCvv,
      invalidHolder: t.invalidHolder,
    });

    if (result.errors) {
      setBloominFieldErrors(result.errors);
      return null;
    }

    return result.payload;
  }, [bloominCard, t.invalidCard, t.invalidCvv, t.invalidExpiry, t.invalidHolder]);

  const handleBloominSubmit = useCallback(async (withNickname: boolean) => {
    const payload = buildBloominPayload();
    if (!payload) return;

    try {
      await onConfirmBloominRegistration(
        payload,
        withNickname ? nickname.trim() || undefined : undefined
      );
      setStep('success');
    } catch (err) {
      logger.error('[AddCardModal] Bloomin registration failed:', toSafeBloominErrorLog(err));
      setError(t.errorMessage);
      setStep('error');
    } finally {
      setBloominCard(initialBloominCardForm);
      setBloominFieldErrors({});
    }
  }, [buildBloominPayload, nickname, onConfirmBloominRegistration, t.errorMessage]);

  /**
   * 재시도 - 상태 초기화 후 useEffect가 체크아웃 재생성
   */
  const handleRetry = useCallback(() => {
    setStep('init');
    setError(null);
    setBloominCard(initialBloominCardForm);
    setBloominFieldErrors({});
    hasCalledCheckoutRef.current = false;
    // useEffect가 자동으로 체크아웃 생성 시작
  }, []);

  /**
   * 닫기
   */
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 - 키보드 접근성 추가 (#145) */}
      <div
        className="absolute inset-0 bg-black/50"
        role={step === 'form' ? undefined : 'button'}
        tabIndex={step === 'form' ? -1 : 0}
        onClick={step === 'form' ? undefined : handleClose}
        onKeyDown={(e) => {
          if (step !== 'form' && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClose();
          }
        }}
        aria-label={step === 'form' ? undefined : 'Close modal'}
      />

      {/* 모달 컨텐츠 */}
      <div className="relative bg-background rounded-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* 헤더 */}
        <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{t.title}</h3>
              <p className="text-sm text-muted-foreground">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-muted-foreground hover:text-foreground rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            type="button"
            aria-label="Close"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="p-6">
          {/* 초기화 / 체크아웃 생성 중 */}
          {(step === 'init' || isCreatingCheckout) && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {isCreatingCheckout ? t.creatingCheckout : t.loading}
              </p>
            </div>
          )}

          {/* 카드 입력 폼 */}
          {step === 'form' && checkout?.gateway === 'bloomin' && canRenderRegistrationSession(checkout) && (
            <form
              className="space-y-4"
              // #430 codex: native constraint validation(required)이 빈 제출을 먼저 막으면
              // 커스텀 검증(필드별 에러 + aria-invalid)이 적용되지 않는다. noValidate 로
              // 브라우저 검증을 끄고 handleBloominSubmit 의 커스텀 검증으로 일원화한다.
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void handleBloominSubmit(true);
              }}
            >
              {isConfirming ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">{t.confirmingRegistration}</p>
                </div>
              ) : (
                <>
                  <BloominCardFields
                    value={bloominCard}
                    onChange={setBloominCard}
                    onFieldChange={clearBloominFieldError}
                    fieldErrors={bloominFieldErrors}
                    labels={{
                      cardNumberLabel: t.cardNumberLabel,
                      cardNumberPlaceholder: '',
                      expiryMonthLabel: t.expiryMonthLabel,
                      expiryYearLabel: t.expiryYearLabel,
                      cvvLabel: t.cvvLabel,
                      cvvPlaceholder: '',
                      holderNameLabel: t.holderLabel,
                      holderNamePlaceholder: '',
                    }}
                    idPrefix="add-card"
                    brandTestId="add-card-brand"
                    disabled={isConfirming}
                  />
                  <label className="block space-y-2">
                    <span className="text-sm font-medium text-foreground">{t.nicknameLabel}</span>
                    <input
                      type="text"
                      value={nickname}
                      onChange={(event) => setNickname(event.target.value)}
                      placeholder={t.nicknamePlaceholder}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      maxLength={50}
                    />
                  </label>

                  <div className="flex flex-col gap-3">
                    <button
                      className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      type="submit"
                      disabled={isConfirming}
                    >
                      {t.saveWithNickname}
                    </button>
                    <button
                      onClick={() => void handleBloominSubmit(false)}
                      className="w-full px-4 py-3 border border-border text-foreground rounded-lg font-medium hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-muted-foreground focus-visible:ring-offset-2"
                      type="button"
                      disabled={isConfirming}
                    >
                      {t.skipNickname}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}

          {/* 성공 */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <CheckCircle className="w-16 h-16 text-primary" />
              <h4 className="text-lg font-semibold text-foreground">
                {t.successTitle}
              </h4>
              <p className="text-sm text-muted-foreground text-center">
                {t.successMessage}
              </p>
              <button
                onClick={handleClose}
                className="mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                type="button"
              >
                {t.done}
              </button>
            </div>
          )}

          {/* 에러 */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="w-16 h-16 text-destructive" />
              <h4 className="text-lg font-semibold text-foreground">
                {t.errorTitle}
              </h4>
              <p className="text-sm text-destructive text-center">
                {error || t.errorMessage}
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleRetry}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  type="button"
                >
                  {t.retry}
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 border border-border text-foreground rounded-lg font-medium hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-muted-foreground focus-visible:ring-offset-2"
                  type="button"
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddCardModal;
